import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, of, delay, Observable } from 'rxjs';

import { InboundService } from '../inbound.service';
import { WarehouseWarrantInfoVO } from '@app/api/model/warehouseWarrantInfoVO';
import {
  WarehouseWarrantDetailItem,
  TrackingPolicy,
} from '../models/warehouse-warrant-detail-item';
import {
  INBOUND_NOTICE_STATUS_OPTIONS,
  InboundWarrantStatus,
} from '../inbound.config';
import { InboundFormWrapperComponent } from '../inbound-form-wrapper/inbound-form-wrapper.component';
import {
  getInboundStatusSeverity,
  getInboundStatusStyle,
  getInboundTypeLabel,
  getInboundStatusLabel,
} from '../inbound.util';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import {
  StatusChangeDialogComponent,
  StatusChangeDialogData,
  StatusChangeResult,
} from './components/status-change-dialog/status-change-dialog.component';
// Import TrackingLine from receive-goods component
import type { TrackingLine } from './components/receive-goods/receive-goods.component';

@Component({
  selector: 'app-inbound-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    TagModule,
    TooltipModule,
    ToastModule,
    InboundFormWrapperComponent,
    StatusChangeDialogComponent,
    // ReceiveGoodsDialogComponent removed - using page instead
  ],
  providers: [MessageService],
  templateUrl: './inbound-edit.component.html',
  styleUrl: './inbound-edit.component.css',
})
export class InboundEditComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly inboundService = inject(InboundService);
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Data
  inboundData = signal<WarehouseWarrantInfoVO | null>(null);
  itemDetails = signal<WarehouseWarrantDetailItem[]>([]);
  loading = signal(false);
  saving = signal(false);
  private skipCanDeactivate = false;

  // Form
  statusForm!: FormGroup;
  statusOptions: any[] = [];

  // Status change dialog
  showStatusDialog = signal(false);
  statusDialogData: StatusChangeDialogData | null = null;

  // Receive goods dialog - removed, using page instead
  // showReceiveDialog = signal(false);
  // receiveDialogData: ReceiveGoodsDialogData | null = null;

  // Status change state
  isStatusChangeConfirmed = signal(false);
  receivedQuantities: { [skuCode: string]: number } = {};
  statusAutoCorrected = signal(false);
  correctedStatus: string | null = null;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.loadInboundData(Number(id));
      this.initializeForm();
    } else {
      this.router.navigate(['/stock/inbound']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.statusForm = this.fb.group({
      status: ['', [Validators.required]],
    });
  }

  onBack(): void {
    // Navigate back to the main inbound page
    // Check for tab query parameter first, then default to inbound-warrant
    const tab = this.route.snapshot.queryParams['tab'] || 'inbound-warrant';

    this.router.navigate(['/stock/inbound'], {
      queryParams: { tab },
    });
  }

  onSave(): void {
    // Mark all form controls as touched to trigger validation display
    this.statusForm.markAllAsTouched();

    if (this.statusForm.valid && this.inboundData()) {
      const currentStatus = this.inboundData()?.status;
      const newStatus = this.statusForm.get('status')?.value;

      // Check if current status is "Receiving" and status change was confirmed
      if (
        this.isCurrentStatusReceiving(currentStatus) &&
        this.isStatusChangeConfirmed()
      ) {
        this.submitInboundWarrantWithQuantities();
      } else {
        // Regular status update
        this.updateInboundStatus(newStatus);
      }
    } else {
      this.saving.set(false); // Reset saving state on validation error
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'inbound.messages.validation_error',
        ),
        detail: this.translateService.instant(
          'inbound.messages.please_select_valid_status',
        ),
      });
    }
  }

  // Helper methods for displaying inbound type and status labels
  getInboundTypeLabel(inputType: string): string {
    return getInboundTypeLabel(inputType, this.translateService);
  }

  getInboundStatusLabel(status: string): string {
    return getInboundStatusLabel(status, this.translateService);
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return getInboundStatusSeverity(status);
  }

  getStatusStyle(status: string): any {
    return getInboundStatusStyle(status);
  }

  formatCreationTime(dateString: string): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid

      // Format as: "15/12/2023 14:30:00"
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return dateString; // Return original if formatting fails
    }
  }

  private loadInboundData(id: number): void {
    this.loading.set(true);

    this.inboundService
      .getWarehouseWarrantDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.inboundData.set(response);

          // Try different possible property names
          const itemDetailsData =
            response?.warehousewarrantDetailList ||
            response?.warehouseWarrantDetailList ||
            response?.warehouseWarrantDetails ||
            response?.itemDetails ||
            response?.items;

          if (
            itemDetailsData &&
            Array.isArray(itemDetailsData) &&
            itemDetailsData.length > 0
          ) {
            this.itemDetails.set(itemDetailsData);
          } else {
            this.itemDetails.set([]);
          }

          this.loading.set(false);

          // Update status options based on statusPossibleNext
          this.updateStatusOptions(response.statusPossibleNext);

          // Only set the current status in the form if statusPossibleNext is empty
          // (meaning no transitions are available)
          if (
            response.status &&
            (!response.statusPossibleNext ||
              response.statusPossibleNext.length === 0)
          ) {
            this.statusForm.patchValue({ status: response.status });
          } else {
            // Clear the form when there are available transitions
            this.statusForm.patchValue({ status: '' });
          }
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.messages.failed_to_load_inbound_warrant_details',
            ),
          });
        },
      });
  }

  private updateStatusOptions(statusPossibleNext?: string[]): void {
    if (statusPossibleNext && statusPossibleNext.length > 0) {
      // Create options from statusPossibleNext with translated labels
      this.statusOptions = statusPossibleNext.map((status) => {
        return {
          label: this.getInboundStatusLabel(status),
          value: status,
        };
      });
    } else if (statusPossibleNext && statusPossibleNext.length === 0) {
      // If statusPossibleNext is empty array, only show current status
      const currentStatus = this.inboundData()?.status;
      if (currentStatus) {
        this.statusOptions = [
          {
            label: this.getInboundStatusLabel(currentStatus),
            value: currentStatus,
          },
        ];
      } else {
        this.statusOptions = [];
      }
    } else {
      // Fallback to all available status options if statusPossibleNext is not provided
      this.statusOptions = INBOUND_NOTICE_STATUS_OPTIONS.map((option) => ({
        label: this.getInboundStatusLabel(option.value),
        value: option.value,
      }));
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.onBack();
    } else if (event.action === 'save') {
      if (this.saving()) {
        return;
      }
      this.saving.set(true); // Set saving state immediately
      this.onSave();
    } else if (event.action === 'go_back') {
      this.onBack();
    }
  }

  onStatusChange(event: { status: string; action?: string }): void {
    const currentStatus = this.inboundData()?.status;
    const newStatus = event.status;

    // Check if this is a receive action (new simplified flow)
    // Also check if status is 'RECEIVE' to ensure we use the new dialog
    if (event.action === 'receive_click' || newStatus === 'RECEIVE') {
      this.handleReceiveAction();
      return;
    }

    // Check if this is a button click action
    if (event.action === 'button_click') {
      this.handleStatusonClick(newStatus);
      return;
    }

    // Original dropdown logic (for backward compatibility)
    // Special case: Skip dialog for "Cancelled" status
    if (newStatus === InboundWarrantStatus.CANCELLED) {
      this.statusForm.patchValue({ status: newStatus });
      this.isStatusChangeConfirmed.set(false);
      return;
    }

    // Check if current status is "Receiving"
    if (this.isCurrentStatusReceiving(currentStatus)) {
      // Show dialog for status change from "Receiving"
      this.showStatusChangeDialog(newStatus);
    } else {
      // For non-Receiving status, allow free status change
      this.statusForm.patchValue({ status: newStatus });
      this.isStatusChangeConfirmed.set(false);
    }
  }

  /**
   * Handle status button click - implements the button-driven status change flow
   */
  private handleStatusonClick(status: string): void {
    const PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED';
    const RECEIVED = 'RECEIVED';

    if (status !== PARTIALLY_RECEIVED && status !== RECEIVED) {
      // Direct update without dialog for non-quantity statuses
      this.updateInboundStatus(status);
    } else {
      // Open change status dialog for quantity input
      this.openStatusDialog(status);
    }
  }

  /**
   * Handle receive action - simplified flow without validation/confirmation dialogs
   */
  private handleReceiveAction(): void {
    // Debug: Check itemDetails before processing
    console.log('=== handleReceiveAction Debug ===');
    console.log('itemDetails():', this.itemDetails());
    console.log('itemDetails length:', this.itemDetails().length);

    // Check if items are available
    if (!this.itemDetails() || this.itemDetails().length === 0) {
      console.error('No items available in itemDetails!');
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.receive.no_items_in_warrant',
        ),
      });
      return;
    }

    // Load SPU policies before opening dialog
    this.loadSpuPolicies().subscribe({
      next: (policiesMap) => {
        // Map policies to items and add GTIN for testing
        const itemsWithPolicy = this.itemDetails().map((item, index) => {
          // Add GTIN for testing GS1-128 barcodes
          // SP1: 12345678901231, SP2: 98765432109876, SP3: 11111111111111
          const gtinMap: { [key: number]: string } = {
            0: '12345678901231', // SP1
            1: '98765432109876', // SP2
            2: '11111111111111', // SP3
          };
          const gtin = gtinMap[index] || item.gtin || item.skuCode;

          return {
            ...item,
            gtin: gtin, // Add GTIN for barcode matching
            policy: item.spuCode ? policiesMap.get(item.spuCode) : undefined,
          };
        });

        console.log('Items with policy and GTIN:', itemsWithPolicy);

        // Navigate to receive goods page instead of opening dialog
        const warrantId = this.route.snapshot.paramMap.get('id');
        if (warrantId) {
          this.router.navigate(['/stock/inbound/receive', warrantId]);
        }
      },
      error: (error) => {
        console.error('Error loading SPU policies:', error);
        // Add GTIN for testing even if policies fail
        const itemsWithGtin = this.itemDetails().map((item, index) => {
          const gtinMap: { [key: number]: string } = {
            0: '12345678901231',
            1: '98765432109876',
            2: '11111111111111',
          };
          const gtin = gtinMap[index] || item.gtin || item.skuCode;
          return {
            ...item,
            gtin: gtin,
          };
        });
        console.log('Items with GTIN (error case):', itemsWithGtin);
        // Navigate to receive goods page anyway without policies
        const warrantId = this.route.snapshot.paramMap.get('id');
        if (warrantId) {
          this.router.navigate(['/stock/inbound/receive', warrantId]);
        }
      },
    });
  }

  /**
   * Mock API call to get SPU policies
   * TODO: Replace with real API call when backend is ready
   * Expected endpoint: GET /v1/goods/spu/profiles?spuCodes=SPU001,SPU002,...
   */
  private loadSpuPolicies(): Observable<Map<string, TrackingPolicy>> {
    const items = this.itemDetails();
    const spuCodes = items
      .map((item) => item.spuCode)
      .filter((code): code is string => !!code)
      .filter((code, index, self) => self.indexOf(code) === index); // Remove duplicates

    if (spuCodes.length === 0) {
      return of(new Map());
    }

    // Mock API response - simulate network delay
    return of(this.getMockSpuPolicies(spuCodes)).pipe(
      delay(300), // Simulate API call delay
    );
  }

  /**
   * Get mock SPU policies
   * TODO: Replace with real API call
   */
  private getMockSpuPolicies(spuCodes: string[]): Map<string, TrackingPolicy> {
    const policiesMap = new Map<string, TrackingPolicy>();

    // Mock policies - some require expiry, some require lot, etc.
    spuCodes.forEach((spuCode, index) => {
      // Vary policies for different SPUs
      if (index % 2 === 0) {
        // Even index: require expiry
        policiesMap.set(spuCode, {
          requireExpiry: true,
          requireLot: true,
          requireSerial: true,
        });
      } else {
        // Odd index: require lot and expiry
        policiesMap.set(spuCode, {
          requireExpiry: true,
          requireLot: true,
          requireSerial: true,
        });
      }
    });

    return policiesMap;
  }

  /**
   * Get mock tracking lines for UI review
   * TODO: Remove this when API is ready
   */
  private getMockTrackingLines(): TrackingLine[] {
    const items = this.itemDetails();
    if (!items || items.length === 0) {
      return [];
    }

    const firstItem = items[0];
    // Ensure secondItem is different from firstItem
    const secondItem = items.length > 1 ? items[1] : null;

    // If only 1 item, create a different mock SKU for SKU 2
    const sku2Code = secondItem
      ? secondItem.skuCode || '98765432109876'
      : '98765432109876';
    const sku2Name = secondItem
      ? secondItem.skuName || 'Sản phẩm Test 2'
      : 'Sản phẩm Test 2';
    const sku2Spu = secondItem ? secondItem.spuCode || 'SPU002' : 'SPU002';

    return [
      // SP1 - Ví dụ 1: Cùng sản phẩm, cùng số lô (GS1-128: 011234567890123110LOT001171231)
      {
        id: 'mock-1',
        skuCode: firstItem.skuCode || '12345678901231',
        skuName: firstItem.skuName || 'Sản phẩm 1',
        spuCode: firstItem.spuCode || 'SPU001',
        lot: 'LOT001',
        expiry: '2017-12-31', // 171231 in GS1-128 format
        serial: undefined,
        quantity: 1,
        plannedQty: firstItem.plannedQty || 50,
        policy: firstItem.policy,
      },
      // SP1 - Ví dụ 2: Cùng sản phẩm, khác số lô (GS1-128: 011234567890123110LOT002171231)
      {
        id: 'mock-2',
        skuCode: firstItem.skuCode || '12345678901231',
        skuName: firstItem.skuName || 'Sản phẩm 1',
        spuCode: firstItem.spuCode || 'SPU001',
        lot: 'LOT002',
        expiry: '2017-12-31', // 171231 in GS1-128 format
        serial: undefined,
        quantity: 1,
        plannedQty: firstItem.plannedQty || 50,
        policy: firstItem.policy,
      },
      // SP2 - Ví dụ 3: Khác sản phẩm (GS1-128: 019876543210987610LOT001171231)
      {
        id: 'mock-3',
        skuCode: sku2Code || '98765432109876',
        skuName: sku2Name || 'Sản phẩm 2',
        spuCode: sku2Spu || 'SPU002',
        lot: 'LOT001',
        expiry: '2017-12-31', // 171231 in GS1-128 format
        serial: undefined,
        quantity: 1,
        plannedQty: secondItem?.plannedQty || 30,
        policy: secondItem?.policy,
      },
      // SP3 - Thêm sản phẩm 3 để test
      {
        id: 'mock-4',
        skuCode:
          items.length > 2
            ? items[2].skuCode || '11111111111111'
            : '11111111111111',
        skuName:
          items.length > 2 ? items[2].skuName || 'Sản phẩm 3' : 'Sản phẩm 3',
        spuCode: items.length > 2 ? items[2].spuCode || 'SPU003' : 'SPU003',
        lot: 'LOT001',
        expiry: '2017-12-31',
        serial: undefined,
        quantity: 1,
        plannedQty: items.length > 2 ? items[2].plannedQty || 20 : 20,
        policy: items.length > 2 ? items[2].policy : undefined,
      },
    ];
  }

  private shouldShowStatusDialog(
    currentStatus: string | undefined,
    newStatus: string,
  ): boolean {
    // Show dialog when changing from RECEIVING to other statuses
    // Also check for case-insensitive comparison in case of data inconsistency
    const isCurrentReceiving =
      currentStatus === InboundWarrantStatus.RECEIVING ||
      currentStatus?.toUpperCase() === 'RECEIVING';
    const isNewDifferent =
      newStatus !== InboundWarrantStatus.RECEIVING &&
      newStatus?.toUpperCase() !== 'RECEIVING';

    return isCurrentReceiving && isNewDifferent;
  }

  private showStatusChangeDialog(newStatus: string): void {
    // Don't show old dialog if this is a RECEIVE action - use new dialog instead
    if (newStatus === 'RECEIVE') {
      this.handleReceiveAction();
      return;
    }

    if (!this.inboundData() || !this.itemDetails().length) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.messages.no_items_to_update',
        ),
      });
      return;
    }

    // Ensure all items have receivedQty property initialized
    const itemsWithReceivedQty = this.itemDetails().map((item) => ({
      ...item,
      receivedQty: item.receivedQty || 0,
    }));

    this.statusDialogData = {
      items: itemsWithReceivedQty,
      newStatus: newStatus,
      totalQty: this.inboundData()?.totalQty, // Pass totalQty to dialog
    };
    this.showStatusDialog.set(true);
  }

  /**
   * Open status dialog for button-driven status changes
   * This is the same as showStatusChangeDialog but with button-specific logging
   */
  private openStatusDialog(status: string): void {
    this.showStatusChangeDialog(status);
  }

  private updateInboundStatus(newStatus: string): void {
    const inboundData = this.inboundData();
    if (!inboundData?.id) {
      this.saving.set(false); // Reset saving state on validation error
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.messages.status_update_failed',
        ),
      });
      return;
    }

    // Real API call for updating status
    this.inboundService
      .updateInboundStatus(inboundData.id, newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.statusForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'inbound.messages.inbound_warrant_status_updated_successfully',
            ),
          });
          // Navigate back to the management page after successful update
          setTimeout(() => {
            const tab =
              this.route.snapshot.queryParams['tab'] || 'inbound-warrant';
            this.router.navigate(['/stock/inbound'], {
              queryParams: { tab },
            });
          }, 1000); // Small delay to show the success message
        },
        error: (error) => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.messages.failed_to_update_inbound_warrant_status',
            ),
          });
        },
      });
  }

  onStatusDialogResult(result: StatusChangeResult): void {
    if (result.confirmed && result.data) {
      // Store received quantities
      this.receivedQuantities = {};
      result.data.items.forEach((item) => {
        // Use the receivedQty property
        const receivedQty = item.receivedQty || 0;
        this.receivedQuantities[item.skuCode || ''] = receivedQty;
      });

      this.submitInboundWarrantWithQuantities();
    }

    // Close the status dialog only if validation passes or user cancels
    this.showStatusDialog.set(false);
    this.statusDialogData = null;
  }

  onDialogVisibilityChange(visible: boolean): void {
    this.showStatusDialog.set(visible);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.saving()) {
      return true;
    }
    return !(this.statusForm?.dirty ?? false);
  }

  // Receive goods dialog handlers - removed, using page instead
  // onReceiveDialogVisibilityChange(visible: boolean): void {
  //   this.showReceiveDialog.set(visible);
  //   // Ensure old dialog is closed when new dialog opens
  //   if (visible) {
  //     this.showStatusDialog.set(false);
  //     this.statusDialogData = null;
  //   }
  // }

  // onReceiveDialogResult(result: ReceiveGoodsResult): void {
  //   if (result.confirmed && result.trackingLines) {
  //     // Transform tracking lines to batch payload format
  //     this.submitInboundWarrantWithBatches(result.trackingLines);
  //   }

  //   // Close the receive dialog
  //   this.showReceiveDialog.set(false);
  //   this.receiveDialogData = null;
  // }

  // Helper method to check if current status is "Receiving"
  private isCurrentStatusReceiving(currentStatus: string | undefined): boolean {
    return (
      currentStatus === InboundWarrantStatus.RECEIVING ||
      currentStatus?.toUpperCase() === 'RECEIVING'
    );
  }

  // Validate and auto-correct status based on received quantities
  private validateAndCorrectStatus(
    selectedStatus: string,
    items: any[],
  ): string {
    const allQuantitiesMatch = items.every((item) => {
      const plannedQty =
        this.itemDetails().find((detail) => detail.skuCode === item.id)
          ?.plannedQty || 0;
      return item.receivedQuantity === plannedQty;
    });

    const anyQuantityLess = items.some((item) => {
      const plannedQty =
        this.itemDetails().find((detail) => detail.skuCode === item.id)
          ?.plannedQty || 0;
      return item.receivedQuantity < plannedQty;
    });

    // Auto-correct logic - use includes() for more robust comparison
    const normalizedSelectedStatus = selectedStatus?.trim().toUpperCase();

    const isReceived =
      normalizedSelectedStatus?.includes('RECEIVED') &&
      !normalizedSelectedStatus?.includes('PARTIALLY');
    const isPartiallyReceived =
      normalizedSelectedStatus?.includes('PARTIALLY') &&
      normalizedSelectedStatus?.includes('RECEIVED');

    if (isReceived && anyQuantityLess) {
      return InboundWarrantStatus.PARTIALLY_RECEIVED;
    } else if (isPartiallyReceived && allQuantitiesMatch) {
      return InboundWarrantStatus.RECEIVED;
    }

    return selectedStatus;
  }

  // Submit inbound warrant with received quantities
  private submitInboundWarrantWithQuantities(): void {
    const inboundData = this.inboundData();
    if (!inboundData?.id) {
      this.saving.set(false); // Reset saving state on validation error
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.messages.status_update_failed',
        ),
      });
      return;
    }

    // Validate received quantities
    const validationErrors = this.validateReceivedQuantities();
    if (validationErrors.length > 0) {
      this.saving.set(false); // Reset saving state on validation error
      validationErrors.forEach((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'inbound.messages.validation_error',
          ),
          detail: error,
        });
      });
      return;
    }

    this.saving.set(true);
    // Prepare submit data
    const submitData = {
      details: Object.entries(this.receivedQuantities).map(
        ([skuCode, receivedQuantity]) => ({
          skuCode,
          receivedQuantity,
        }),
      ),
    };

    // Call submit API
    this.inboundService
      .submitInboundWarrant(inboundData.id, submitData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.statusForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'inbound.messages.inbound_warrant_status_updated_successfully',
            ),
          });

          // Navigate back after successful submit
          setTimeout(() => {
            const tab =
              this.route.snapshot.queryParams['tab'] || 'inbound-warrant';
            this.router.navigate(['/stock/inbound'], { queryParams: { tab } });
          }, 1000);
        },
        error: (error) => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.messages.failed_to_update_inbound_warrant_status',
            ),
          });
        },
      });
  }

  // Validate received quantities
  private validateReceivedQuantities(): string[] {
    const errors: string[] = [];

    // Check if at least one item has received quantity > 0
    const hasAtLeastOneReceivedQty = this.itemDetails().some((item) => {
      const receivedQty = this.receivedQuantities[item.skuCode || ''] || 0;
      return receivedQty > 0;
    });

    if (!hasAtLeastOneReceivedQty) {
      errors.push(
        this.translateService.instant(
          'inbound.messages.at_least_one_received_quantity_required',
        ),
      );
    }

    this.itemDetails().forEach((item, index) => {
      const receivedQty = this.receivedQuantities[item.skuCode || ''] || 0;
      // Use totalQty from inbound data as planned quantity for validation
      const plannedQty = this.inboundData()?.totalQty || item.plannedQty || 0;

      // Rule: Received Quantity must be ≤ Maximum Allowed (planned - already received)
      // Allow 0 values, but validate against maximum
      if (receivedQty > plannedQty) {
        errors.push(
          this.translateService.instant(
            'inbound.messages.received_quantity_exceeds_planned',
            {
              row: index + 1,
              received: receivedQty,
              planned: plannedQty,
            },
          ),
        );
      }
    });

    return errors;
  }

  /**
   * Submit only received quantities without status (for RECEIVE action)
   */
  private submitReceivedQuantitiesOnly(): void {
    const inboundData = this.inboundData();
    if (!inboundData?.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.messages.status_update_failed',
        ),
      });
      this.saving.set(false);
      return;
    }

    // Prepare submit data with only quantities (no status)
    const submitData = {
      details: Object.entries(this.receivedQuantities).map(
        ([skuCode, receivedQuantity]) => ({
          skuCode,
          receivedQuantity,
        }),
      ),
    };

    // Call submit API (backend will determine status based on quantities)
    this.inboundService
      .submitInboundWarrant(inboundData.id, submitData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.statusForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'inbound.messages.inbound_warrant_status_updated_successfully',
            ),
          });

          // Navigate back after successful submit
          setTimeout(() => {
            const tab =
              this.route.snapshot.queryParams['tab'] || 'inbound-warrant';
            this.router.navigate(['/stock/inbound'], { queryParams: { tab } });
          }, 1000);
        },
        error: (error) => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.messages.failed_to_update_inbound_warrant_status',
            ),
          });
        },
      });
  }

  /**
   * Submit inbound warrant with batches (tracking lines)
   * Payload format:
   * {
   *   details: [
   *     {
   *       skuCode: "211110679",
   *       receivedQuantity: 4,
   *       batches: [
   *         {
   *           lotNumber: "LOT-20231101",
   *           expiryDate: "2026-11-01",
   *           serials: ["SN-A-000123", "SN-A-000124"],
   *           qty: 2
   *         }
   *       ]
   *     }
   *   ]
   * }
   */
  private submitInboundWarrantWithBatches(trackingLines: TrackingLine[]): void {
    const inboundData = this.inboundData();
    if (!inboundData?.id) {
      this.saving.set(false);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.messages.status_update_failed',
        ),
      });
      return;
    }

    // Validate tracking lines
    const validationErrors = this.validateTrackingLines(trackingLines);
    if (validationErrors.length > 0) {
      this.saving.set(false);
      validationErrors.forEach((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'inbound.messages.validation_error',
          ),
          detail: error,
        });
      });
      return;
    }

    this.saving.set(true);

    // Transform tracking lines to batch payload format
    const submitData = this.transformTrackingLinesToBatchPayload(trackingLines);

    // Call submit API
    this.inboundService
      .submitInboundWarrant(inboundData.id, submitData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.statusForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'inbound.messages.inbound_warrant_status_updated_successfully',
            ),
          });

          // Navigate back after successful submit
          setTimeout(() => {
            const tab =
              this.route.snapshot.queryParams['tab'] || 'inbound-warrant';
            this.router.navigate(['/stock/inbound'], { queryParams: { tab } });
          }, 1000);
        },
        error: (error) => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.messages.failed_to_update_inbound_warrant_status',
            ),
          });
        },
      });
  }

  /**
   * Transform tracking lines to batch payload format
   * Group by SKU, then by LOT, aggregate quantities and serials
   */
  private transformTrackingLinesToBatchPayload(
    trackingLines: TrackingLine[],
  ): any {
    // Group by SKU
    const skuMap = new Map<string, TrackingLine[]>();
    trackingLines.forEach((line) => {
      if (!skuMap.has(line.skuCode)) {
        skuMap.set(line.skuCode, []);
      }
      skuMap.get(line.skuCode)!.push(line);
    });

    // Transform to payload format
    const details = Array.from(skuMap.entries()).map(([skuCode, lines]) => {
      // Calculate total received quantity for this SKU
      const receivedQuantity = lines.reduce(
        (sum, line) => sum + line.quantity,
        0,
      );

      // Group by LOT (lotNumber + expiryDate combination)
      const lotMap = new Map<string, TrackingLine[]>();
      lines.forEach((line) => {
        const lotKey = `${line.lot || ''}_${line.expiry || ''}`;
        if (!lotMap.has(lotKey)) {
          lotMap.set(lotKey, []);
        }
        lotMap.get(lotKey)!.push(line);
      });

      // Transform to batches
      const batches = Array.from(lotMap.values()).map((lotLines) => {
        const firstLine = lotLines[0];

        // Collect all serials from lines in this lot
        const serials: string[] = [];
        lotLines.forEach((line) => {
          if (line.serial) {
            serials.push(line.serial);
          }
        });

        // Calculate total quantity for this batch
        const batchQty = lotLines.reduce((sum, line) => sum + line.quantity, 0);

        return {
          lotNumber: firstLine.lot || undefined,
          expiryDate: firstLine.expiry || undefined,
          serials: serials.length > 0 ? serials : undefined,
          qty: batchQty,
        };
      });

      return {
        skuCode,
        receivedQuantity,
        batches,
      };
    });

    return { details };
  }

  /**
   * Validate tracking lines before submit
   */
  private validateTrackingLines(trackingLines: TrackingLine[]): string[] {
    const errors: string[] = [];

    if (!trackingLines || trackingLines.length === 0) {
      errors.push(
        this.translateService.instant(
          'inbound.receive.at_least_one_tracking_line_required',
        ),
      );
      return errors;
    }

    // Validate each tracking line based on policy
    trackingLines.forEach((line, index) => {
      if (line.policy) {
        if (line.policy.requireLot && !line.lot) {
          errors.push(
            `Line ${index + 1}: ${this.translateService.instant(
              'inbound.fields.lot',
            )} is required`,
          );
        }
        if (line.policy.requireExpiry && !line.expiry) {
          errors.push(
            `Line ${index + 1}: ${this.translateService.instant(
              'inbound.fields.expiry',
            )} is required`,
          );
        }
        if (line.policy.requireSerial && !line.serial) {
          errors.push(
            `Line ${index + 1}: ${this.translateService.instant(
              'inbound.fields.serial',
            )} is required`,
          );
        }
      }
    });

    return errors;
  }
}
